import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { warmAudioContext } from './QueueScreen';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

export const AVATARS = [
  { id: 'smile',     emoji: '😀' },
  { id: 'cool',      emoji: '😎' },
  { id: 'star',      emoji: '🤩' },
  { id: 'party',     emoji: '🥳' },
  { id: 'cat',       emoji: '😺' },
  { id: 'fox',       emoji: '🦊' },
  { id: 'penguin',   emoji: '🐧' },
  { id: 'sparkle',   emoji: '🌟' },
  { id: 'robot',     emoji: '🤖' },
  { id: 'alien',     emoji: '👽' },
  { id: 'unicorn',   emoji: '🦄' },
  { id: 'bear',      emoji: '🐻' },
  { id: 'dog',       emoji: '🐶' },
  { id: 'octopus',   emoji: '🐙' },
  { id: 'butterfly', emoji: '🦋' },
  { id: 'dragon',    emoji: '🐉' },
  { id: 'astronaut', emoji: '🚀' },
  { id: 'wizard',    emoji: '🧙' },
];

function randomAvatar() {
  return AVATARS[Math.floor(Math.random() * AVATARS.length)];
}

export default function JoinScreen({ onJoin, onRecover }) {
  const { t } = useTranslation();
  const [avatarId, setAvatarId] = useState(() => randomAvatar().id);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const turnstileEnabled = !!TURNSTILE_SITE_KEY;

  const resetTurnstile = useCallback(() => {
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken('');
    }
  }, []);

  useEffect(() => {
    if (!turnstileEnabled || !turnstileRef.current) return;
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
          theme: 'dark',
          size: 'normal',
        });
      }
    }, 100);
    return () => {
      clearInterval(interval);
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [turnstileEnabled]);

  function handleSubmit(e) {
    e.preventDefault();
    if (turnstileEnabled && !turnstileToken) return;
    warmAudioContext();
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    const avatar = AVATARS.find(a => a.id === avatarId);
    onJoin({ avatarId, avatarName: t(`avatars.${avatar.id}`), turnstileToken });
  }

  const selected = AVATARS.find(a => a.id === avatarId);

  return (
    <div style={s.card}>
      <h1 style={s.title}>{t('join.title')}</h1>
      <p style={s.subtitle}>{t('join.subtitle')}</p>

      <div
        style={s.avatarGrid}
        role="radiogroup"
        aria-label={t('join.avatarGroupLabel')}
      >
        {AVATARS.map(({ id, emoji }) => {
          const name = t(`avatars.${id}`);
          const isSelected = avatarId === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => setAvatarId(id)}
              style={{
                ...s.avatarBtn,
                ...(isSelected ? s.avatarBtnActive : {}),
              }}
              aria-label={name}
              title={name}
            >
              {emoji}
            </button>
          );
        })}
      </div>

      {selected && (
        <p style={s.selectedName} aria-live="polite">{selected.emoji} {t(`avatars.${selected.id}`)}</p>
      )}

      <form onSubmit={handleSubmit} style={s.form}>
        {turnstileEnabled && (
          <div ref={turnstileRef} style={{ display: 'flex', justifyContent: 'center' }} />
        )}
        <button type="submit" style={{
          ...s.primaryBtn,
          ...(turnstileEnabled && !turnstileToken ? s.primaryBtnDisabled : {}),
        }} disabled={turnstileEnabled && !turnstileToken}>
          {t('join.joinButton')}
        </button>
      </form>

      <button type="button" onClick={onRecover} style={s.linkBtn}>
        {t('join.recoverLink')}
      </button>
    </div>
  );
}

const s = {
  card: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 16, width: '100%',
  },
  title: { fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#F8FAFC' },
  subtitle: { fontSize: '0.9rem', color: '#94A3B8', margin: 0, textAlign: 'center' },
  avatarGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 8, width: '100%',
  },
  avatarBtn: {
    fontSize: '2rem', background: '#1E293B', border: '2px solid transparent',
    borderRadius: 12, padding: '10px 0', cursor: 'pointer', transition: 'all 0.15s',
  },
  avatarBtnActive: {
    border: '2px solid #7C3AED', background: 'rgba(124,58,237,0.12)',
  },
  selectedName: {
    margin: 0, fontWeight: 600, fontSize: '1rem', color: '#F8FAFC',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 10, width: '100%' },
  primaryBtn: {
    fontSize: '1rem', fontWeight: 600, padding: '13px', borderRadius: 12,
    border: 'none', background: '#7C3AED', color: '#fff', cursor: 'pointer',
    width: '100%',
  },
  primaryBtnDisabled: {
    opacity: 0.5, cursor: 'not-allowed',
  },
  linkBtn: {
    background: 'none', border: 'none', color: '#7C3AED',
    fontSize: '0.875rem', cursor: 'pointer', textDecoration: 'underline',
  },
};
